import FirstLineContainer from "components/common/FirstLineContainer";
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PolicyFilter from "./Filter";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import StyledDataGrid from "styles/styledDataGrid";
import api from "api";
import { UserContext } from "context";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import { ALL } from "constants/Constants";
import {
  markHighestVersionPolicies,
  getPolicyVersions,
} from "./utils/policyConfigUtil";
import { formErrorStrfromObj } from "utils/shared";
import CustomGridToolbar from "components/GridToolbar";

const PolicyConfiguration = () => {
  const [loading, setLoading] = useState(false);
  const [policyData, setPolicyData] = useState([]);
  const policyTypeOptionsRef = useRef([]);
  const [latestOnly, setLatestOnly] = useState(false);
  const [selectedContract, setSelectedContract] = useState(ALL);
  const [selectedCab, setSelectedCab] = useState(ALL);

  const userContext = useContext(UserContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAdmin = useMemo(() => {
    if (!userContext) return false;
    return (
      userContext.groups[0] === "POET-Avalon-Admin" ||
      (userContext.POETSkill &&
        userContext.POETSkill.some((skill) => skill.toLowerCase() === "admin"))
    );
  }, [userContext]);

  useEffect(() => {
    if (userContext) {
      fetchPolicyData();
    }
  }, [userContext]);

  const handleAddUpdatePolicy = (row, isAdminFlag) => {
    // edit / view flow
    if (row?.policyKey) {
      const allVersions = getPolicyVersions(row.policyNumber, policyData);
      navigate(`/policy-config/update/${row.policyKey}`, {
        state: {
          isAdmin: isAdminFlag,
          policyTypeOptions: policyTypeOptionsRef.current,
          policyNumber: row.policyNumber,
          policyTitle: row.policyTitle,
          versionText: row.versionText,
          versionFull: row.versionFull,
          versionHistory: allVersions,
          isHighestVersion: row.isHighestVersion,
        },
      });
      return;
    }

    // create flow
    if (isAdminFlag) {
      navigate(`/policy-config/create`, {
        state: {
          isAdmin: isAdminFlag,
          policyTypeOptions: policyTypeOptionsRef.current,
        },
      });
    } else {
      dispatch(
        setDialog({
          message: "You do not have permission to add a new policy.",
          title: "Permission Denied",
        })
      );
    }
  };

  // Filtered Data Memoized for Performance
  const filteredData = useMemo(() => {
    return policyData.filter((row) => {
      const matchesVersion = !latestOnly || row.isHighestVersion;

      const matchesType =
        selectedContract === ALL ||
        row.policyType?.toLowerCase() === selectedContract.toLowerCase() ||
        (selectedContract.toLowerCase() === "p/gtm" &&
          (row.policyType?.toLowerCase() === "gtm" ||
            row.policyType?.toLowerCase() === "pgtm"));

      const matchesCab =
        selectedCab === ALL ||
        row.quarter?.toLowerCase() === selectedCab.toLocaleLowerCase();
      return matchesVersion && matchesType && matchesCab;
    });
  }, [policyData, latestOnly, selectedContract, selectedCab]);

  const columns = [
    { field: "policyKey", headerName: "Policy Key", flex: 0.5 },
    { field: "policyNumber", headerName: "Policy Number", flex: 1 },
    {
      field: "policyTitle",
      headerName: "Policy Title",
      flex: 2,
    },
    {
      field: "versionFull",
      headerName: "Policy Version",
      flex: 1,
    },
    {
      field: "lastCab",
      headerName: "Last CAB",
      flex: 1,
      valueGetter: (params, row) => {
        const quarter = row.quarter;
        const year = row.year;
        const shortYear = year ? year.toString().slice(-2) : "";
        return quarter && shortYear ? `${quarter} ${shortYear}` : "";
      },
    },
    { field: "policyType", headerName: "Policy Type", flex: 1 },
  ];

  const fetchPolicyData = async () => {
    setLoading(true);
    try {
      const response =
        await api.PolicyConfiguration.getAllPolicyConfigurations();
      const processedData = markHighestVersionPolicies(response.data);
      setPolicyData(processedData);

      const ptResponse = await api.PolicyConfiguration.getPolicyTypeData();
      policyTypeOptionsRef.current = ptResponse.data;
    } catch (error) {
      dispatch(
        setDialog({
          message:
            formErrorStrfromObj(error) ||
            "Failed to retrieve the List of Policies",
          title: error.message || "Error",
        })
      );
      console.error("Error fetching policy configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  // This is the LEFT SIDE content we want in the shared CustomGridToolbar
  const exportComponent = (
    <Stack direction="row" spacing={2} alignItems="center">
      {isAdmin && (
        <Button
          data-testid="create-policy-button"
          variant="contained"
          size="small"
          onClick={() => handleAddUpdatePolicy(null, isAdmin)}
          disabled={loading}
        >
          Create Policy
        </Button>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={latestOnly}
            onChange={(e) => setLatestOnly(e.target.checked)}
            size="small"
          />
        }
        label="Show Latest Only"
        sx={{ m: 0 }}
      />
    </Stack>
  );

  if (!userContext) return null;

  return (
    <>
      <Box
        data-testid="policy-conf"
        sx={{ height: "90vh", display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={1} marginBottom={1}>
          <FirstLineContainer>
            <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
              <Box
                data-testid="policy-conf-title"
                component="span"
                fontWeight="fontWeightBold"
              >
                Policy Configuration
              </Box>
            </Typography>
          </FirstLineContainer>

          <PolicyFilter
            data-testid="policy-config-filter"
            policyTypeOptions={policyTypeOptionsRef.current}
            selectedContract={selectedContract}
            setSelectedContract={setSelectedContract}
            selectedCab={selectedCab}
            setSelectedCab={setSelectedCab}
          />
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
          }}
        >
          <StyledDataGrid
            data-testid="policy-config-policy-grid"
            columns={columns}
            rows={filteredData}
            loading={loading}
            rowHeight={40}
            getRowId={(row) => row.policyKey}
            disableExtendRowFullWidth
            onRowClick={(params) => handleAddUpdatePolicy(params.row, isAdmin)}
            slots={{
              toolbar: CustomGridToolbar,
            }}
            slotProps={{
              toolbar: {
                exportComponent,
              },
            }}
            getRowClassName={(params) =>
              params.row.isHighestVersion ? "dark-row" : "light-row"
            }
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
              "& .dark-row": {
                color: "black",
              },
              "& .light-row": {
                color: "gray",
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default PolicyConfiguration;
